import { useCopy } from '#lib/use-copy.js';

import { DetailRow } from './ItineraryDetailRow.js';

interface ItineraryDetailSummaryProps {
  /** A short, human description of the place/hotel (clamped to a few lines). */
  description?: string | null;
  /** Opens the relevant guide; rendered as a "View full guide" row beneath the description. */
  onShowGuide?: () => void;
  /** A short address / locality. */
  location?: string | null;
  /** A maps URL for the location, used to make the address a "directions" link. */
  directionsUrl?: string | null;
  /** Absolute website URL (opened in a new tab). */
  website?: string | null;
  /** Contact phone number (dialed via a `tel:` link). */
  phone?: string | null;
  /** Open/closed status + next transition (e.g. "Opens 8:30 AM Thu"); renders an hours row. */
  hours?: { openNow: boolean; detail?: string };
  /** Click handler for the hours row (e.g. open the place guide). */
  onShowHours?: () => void;
  /** Formatted price string (e.g. "£45"); renders a payments row when present. */
  cost?: string | null;
  /** Secondary cost label (e.g. "per person"); shown alongside the cost. */
  costDetail?: string | null;
}

/** Trims a URL down to a readable host (drops protocol + `www.`) for display. */
function websiteLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * The shared "what is this?" block shown when an itinerary event is expanded: a short (clamped)
 * description, then Google-Maps-style icon rows — location / hours / website / phone / guide link.
 * Notes live separately in ItineraryNotesSection.
 */
export function ItineraryDetailSummary({
  description,
  onShowGuide,
  location,
  directionsUrl,
  website,
  phone,
  hours,
  onShowHours,
  cost,
  costDetail,
}: ItineraryDetailSummaryProps) {
  const copy = useCopy();
  const hasRows = Boolean(onShowGuide || location || directionsUrl || website || phone || hours || cost);

  if (!description && !hasRows) return null;

  return (
    <div className="flex flex-col gap-3 text-sm">
      {description && <p className="max-w-prose text-pretty line-clamp-3">{description}</p>}

      {hasRows && (
        <div className="flex flex-col">
          {cost && <DetailRow icon="payments" value={cost} secondary={costDetail ?? undefined} />}

          {(location || directionsUrl) && (
            <DetailRow
              icon="place"
              value={location ?? 'Get directions'}
              onClick={directionsUrl ? () => window.open(directionsUrl, '_blank') : undefined}
              tooltip={directionsUrl ? 'Get directions' : undefined}
            />
          )}

          {hours && (
            <DetailRow
              // "Open now / Closed" is live status: meaningless on paper.
              className="print:hidden"
              icon="schedule"
              value={
                <span>
                  <span className={hours.openNow ? 'text-success' : 'text-destructive'}>
                    {hours.openNow ? 'Open' : 'Closed'}
                  </span>
                  {hours.detail && <span> · {hours.detail}</span>}
                </span>
              }
              secondary="See opening hours"
              onClick={onShowHours}
            />
          )}

          {website && (
            <DetailRow icon="link" value={websiteLabel(website)} onClick={() => window.open(website, '_blank')} />
          )}

          {phone && (
            <DetailRow
              icon="call"
              trailingIcon="content_copy"
              value={phone}
              onClick={() => copy(phone, 'Phone number copied')}
              tooltip="Copy phone number"
            />
          )}

          {onShowGuide && (
            <DetailRow className="print:hidden" icon="more_horiz" value="Read the full guide" onClick={onShowGuide} />
          )}
        </div>
      )}
    </div>
  );
}
